/**
 * Veo API Client
 *
 * Handles communication with Cloud Functions backend for Veo 3.1 video generation.
 * Features: async polling with exponential backoff, localStorage caching, error handling.
 */

/**
 * Animation mode for the plugin
 */
export type AnimationMode = 'auto' | 'css' | 'video';

/**
 * Video generation state
 */
export type VideoState = 'idle' | 'generating' | 'ready' | 'error';

/**
 * Video generation request
 */
export interface VeoGenerationRequest {
    /** Image URL to generate video from */
    imageUrl: string;
    /** Animation prompt for the video */
    animationPrompt?: string;
    /** Aspect ratio (default: auto-detected from image) */
    aspectRatio?: '16:9' | '9:16';
    /** Duration in seconds (default: 4) */
    durationSeconds?: 4 | 6 | 8;
}

/**
 * Video generation response from backend
 */
export interface VeoGenerationResponse {
    success: boolean;
    operationId?: string;
    status?: 'pending' | 'processing' | 'completed' | 'error';
    videoUrl?: string;
    animation?: any; // CSS animation data (from /animate-auto)
    error?: {
        code: string;
        message: string;
    };
}

/**
 * Cached video entry
 */
interface CachedVideo {
    videoUrl: string;
    timestamp: number;
}

/**
 * Configuration for the Veo client
 */
export interface VeoClientConfig {
    /** Backend endpoint URL */
    endpoint: string;
    /** API key for authentication (optional - uses demo limits without) */
    apiKey?: string;
    /** Enable debug logging */
    debug?: boolean;
    /** Polling interval in ms (default: 3000) */
    pollInterval?: number;
    /** Max poll attempts before timeout (default: 60 = 3 minutes) */
    maxPollAttempts?: number;
    /** Cache TTL in ms (default: 30 days) */
    cacheTTL?: number;
}

/**
 * Default configuration values
 */
const DEFAULT_CONFIG = {
    apiKey: '',
    debug: false,
    pollInterval: 3000,
    maxPollAttempts: 60,
    cacheTTL: 30 * 24 * 60 * 60 * 1000, // 30 days
};

/**
 * Cache key prefix for localStorage
 */
const VIDEO_CACHE_PREFIX = 'beautifi_video_';

/**
 * VeoClient class
 *
 * Handles async video generation with automatic polling and caching.
 */
export class VeoClient {
    private config: Required<VeoClientConfig>;
    private pendingOperations: Map<string, AbortController> = new Map();
    /** Session-scoped request ID for tracing across all operations */
    private sessionRequestId: string;

    constructor(config: VeoClientConfig) {
        this.config = {
            ...DEFAULT_CONFIG,
            ...config,
        };
        // Generate unique session ID for request tracing
        this.sessionRequestId = this.generateRequestId();
    }

    /**
     * Generate a unique request ID using crypto.randomUUID or fallback
     */
    private generateRequestId(): string {
        if (typeof crypto !== 'undefined' && crypto.randomUUID) {
            return crypto.randomUUID();
        }
        // Fallback for older browsers
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }

    /**
     * Get current page URL for source tracking
     */
    private getSourcePage(): string {
        if (typeof window !== 'undefined' && window.location) {
            return window.location.href;
        }
        return 'unknown';
    }

    /**
     * Start video generation for an image
     *
     * @param request - Video generation request
     * @returns Promise resolving to operation ID
     */
    async startGeneration(request: VeoGenerationRequest): Promise<VeoGenerationResponse> {
        // Check cache first
        const cacheKey = this.computeCacheKey(request.imageUrl);
        const cached = this.getFromCache(cacheKey);
        if (cached) {
            this.log(`📦 Video cached: ${cacheKey.slice(0, 8)}...`);
            return {
                success: true,
                status: 'completed',
                videoUrl: cached,
            };
        }

        try {
            // Handle both URL patterns:
            // - api.beautifi.uk/animate -> api.beautifi.uk/animate-auto
            // - .../beautifi-animate -> .../beautifi-animate/animate-auto
            let endpoint = this.config.endpoint.replace(/\/$/, ''); // Remove trailing slash
            if (endpoint.includes('/animate')) {
                endpoint = endpoint.replace('/animate', '/animate-auto');
            } else {
                endpoint = `${endpoint}/animate-auto`;
            }

            this.log(`🎬 Starting video generation for: ${request.imageUrl.slice(0, 50)}...`);

            const headers: Record<string, string> = {
                'Content-Type': 'application/json',
                'X-Request-ID': this.sessionRequestId,
                'X-Source-Page': this.getSourcePage(),
            };
            if (this.config.apiKey) {
                headers['X-API-Key'] = this.config.apiKey;
            }

            const response = await fetch(endpoint, {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    imageUrl: request.imageUrl,
                    animationPrompt: request.animationPrompt,
                    aspectRatio: request.aspectRatio,
                    durationSeconds: request.durationSeconds,
                }),
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const data: VeoGenerationResponse = await response.json();

            if (!data.success) {
                throw new Error(data.error?.message || 'Video generation failed');
            }

            this.log(`🚀 Generation started: ${data.operationId}`);
            return data;

        } catch (error) {
            this.log(`❌ Start generation error: ${error}`);
            return {
                success: false,
                error: {
                    code: 'GENERATION_ERROR',
                    message: error instanceof Error ? error.message : 'Failed to start generation',
                },
            };
        }
    }

    /**
     * Poll for video generation status until complete
     *
     * @param operationId - Operation ID to poll
     * @param imageUrl - Original image URL (for caching)
     * @param onProgress - Callback for progress updates
     * @returns Promise resolving to video URL or null on failure
     */
    async pollUntilComplete(
        operationId: string,
        imageUrl: string,
        onProgress?: (status: string, attempt: number) => void
    ): Promise<string | null> {
        const controller = new AbortController();
        this.pendingOperations.set(operationId, controller);

        try {
            let attempt = 0;

            while (attempt < this.config.maxPollAttempts) {
                if (controller.signal.aborted) {
                    this.log(`🛑 Polling aborted: ${operationId}`);
                    return null;
                }

                const result = await this.checkStatus(operationId);

                if (onProgress) {
                    onProgress(result.status || 'unknown', attempt);
                }

                if (result.status === 'completed' && result.videoUrl) {
                    this.log(`✅ Video ready: ${result.videoUrl.slice(0, 50)}...`);

                    // Cache the video URL
                    const cacheKey = this.computeCacheKey(imageUrl);
                    this.saveToCache(cacheKey, result.videoUrl);

                    return result.videoUrl;
                }

                if (result.status === 'error' || !result.success) {
                    this.log(`❌ Generation failed: ${result.error?.message}`);
                    return null;
                }

                // Exponential backoff: 3s, 4.5s, 6.75s, etc. (max 15s)
                const backoffMs = Math.min(
                    this.config.pollInterval * Math.pow(1.5, Math.min(attempt, 5)),
                    15000
                );

                this.log(`⏳ Polling (${attempt + 1}/${this.config.maxPollAttempts}): ${result.status}`);
                await this.sleep(backoffMs);
                attempt++;
            }

            this.log(`⏰ Polling timeout: ${operationId}`);
            return null;

        } finally {
            this.pendingOperations.delete(operationId);
        }
    }

    /**
     * Check the status of a video generation operation
     */
    async checkStatus(operationId: string): Promise<VeoGenerationResponse> {
        try {
            // Handle both URL patterns for status endpoint
            let endpoint = this.config.endpoint.replace(/\/$/, '');
            if (endpoint.includes('/animate')) {
                endpoint = endpoint.replace('/animate', '/generate-video/status');
            } else {
                endpoint = `${endpoint}/generate-video/status`;
            }

            const headers: Record<string, string> = {
                'X-Request-ID': this.sessionRequestId,
            };
            if (this.config.apiKey) {
                headers['X-API-Key'] = this.config.apiKey;
            }

            const response = await fetch(`${endpoint}/${operationId}`, { headers });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            return await response.json();

        } catch (error) {
            return {
                success: false,
                status: 'error',
                error: {
                    code: 'POLL_ERROR',
                    message: error instanceof Error ? error.message : 'Status check failed',
                },
            };
        }
    }

    /**
     * Cancel a pending video generation operation
     */
    cancelOperation(operationId: string): void {
        const controller = this.pendingOperations.get(operationId);
        if (controller) {
            controller.abort();
            this.pendingOperations.delete(operationId);
            this.log(`🛑 Cancelled: ${operationId}`);
        }
    }

    /**
     * Cancel all pending operations
     */
    cancelAllOperations(): void {
        for (const [id, controller] of this.pendingOperations) {
            controller.abort();
            this.log(`🛑 Cancelled: ${id}`);
        }
        this.pendingOperations.clear();
    }

    /**
     * Get cached video URL for an image
     */
    getCachedVideo(imageUrl: string): string | null {
        const cacheKey = this.computeCacheKey(imageUrl);
        return this.getFromCache(cacheKey);
    }

    /**
     * Compute cache key from image URL
     */
    private computeCacheKey(imageUrl: string): string {
        // Simple hash for cache key
        let hash = 0;
        for (let i = 0; i < imageUrl.length; i++) {
            const char = imageUrl.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash; // Convert to 32-bit integer
        }
        return Math.abs(hash).toString(36);
    }

    /**
     * Get video URL from localStorage cache
     */
    private getFromCache(cacheKey: string): string | null {
        if (typeof localStorage === 'undefined') return null;

        try {
            const raw = localStorage.getItem(VIDEO_CACHE_PREFIX + cacheKey);
            if (!raw) return null;

            const cached: CachedVideo = JSON.parse(raw);

            // Check if expired
            if (Date.now() - cached.timestamp > this.config.cacheTTL) {
                localStorage.removeItem(VIDEO_CACHE_PREFIX + cacheKey);
                return null;
            }

            return cached.videoUrl;
        } catch {
            return null;
        }
    }

    /**
     * Save video URL to localStorage cache
     */
    private saveToCache(cacheKey: string, videoUrl: string): void {
        if (typeof localStorage === 'undefined') return;

        try {
            const cached: CachedVideo = {
                videoUrl,
                timestamp: Date.now(),
            };
            localStorage.setItem(VIDEO_CACHE_PREFIX + cacheKey, JSON.stringify(cached));
            this.log(`💾 Cached: ${cacheKey}`);
        } catch {
            // localStorage full or unavailable - ignore
        }
    }

    /**
     * Clear all cached videos
     */
    clearCache(): void {
        if (typeof localStorage === 'undefined') return;

        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key?.startsWith(VIDEO_CACHE_PREFIX)) {
                keysToRemove.push(key);
            }
        }
        keysToRemove.forEach(key => localStorage.removeItem(key));
        this.log(`🧹 Cleared ${keysToRemove.length} cached videos`);
    }

    /**
     * Sleep utility
     */
    private sleep(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    /**
     * Debug logging
     */
    private log(message: string): void {
        if (this.config.debug) {
            console.log(`[VeoClient] ${message}`);
        }
    }
}
